import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const PremiumHeroSection = ({ onNavigate }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleBookNow = () => {
    if (onNavigate) {
      onNavigate("PandithParichayaPage");
    }
  };

  const heroSlides = [
    {
      title: "Find Perfect Purohits",
      subtitle: "For Your Sacred Ceremonies",
      description:
        "Connect with 2000+ verified spiritual guides across 25+ cities",
      image:
        "https://i.pinimg.com/736x/cc/c0/b5/ccc0b5747b6b4b52c0328e1e2092b7d6.jpg",
    },
    {
      title: "Authentic Hindu Rituals",
      subtitle: "Performed With Devotion",
      description: "Traditional ceremonies guided by experienced purohits",
      image:
        "https://i.pinimg.com/736x/53/6b/4e/536b4e705a65d4727e80fdc9bda14b3b.jpg",
    },
    {
      title: "Book Anytime, Anywhere",
      subtitle: "Sacred Services Made Easy",
      description: "Simple online booking for all your spiritual needs",
      image:
        "https://i.pinimg.com/736x/01/a5/8a/01a58a9a1328529c61692161b0bc408c.jpg",
    },
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      clearInterval(interval);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const Flower = ({ delay, size, left, top, duration }) => (
    <div
      className="absolute animate-float-flower hidden sm:block"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 180 180"
        className="drop-shadow-2xl animate-rotate-slow"
        style={{ animationDelay: `${delay}s` }}
      >
        <circle
          cx="50"
          cy="50"
          r="2"
          fill="#FF6B35"
          className="animate-pulse"
        />

        {[0, 72, 144, 216, 288].map((rotation, idx) => (
          <ellipse
            key={idx}
            cx="50"
            cy="30"
            rx="12"
            ry="20"
            fill={`url(#gradient${idx})`}
            transform={`rotate(${rotation} 50 50)`}
            opacity="0.9"
          />
        ))}

        <defs>
          <linearGradient id="gradient0">
            <stop offset="0%" stopColor="#FFB347" />
            <stop offset="100%" stopColor="#FF6B35" />
          </linearGradient>
          <linearGradient id="gradient1">
            <stop offset="0%" stopColor="#FF9A56" />
            <stop offset="100%" stopColor="#FF7F50" />
          </linearGradient>
          <linearGradient id="gradient2">
            <stop offset="0%" stopColor="#FFA500" />
            <stop offset="100%" stopColor="#FF8C00" />
          </linearGradient>
          <linearGradient id="gradient3">
            <stop offset="0%" stopColor="#FFCC80" />
            <stop offset="100%" stopColor="#FFB347" />
          </linearGradient>
          <linearGradient id="gradient4">
            <stop offset="0%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#FFA500" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );

  return (
    <>
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700">
        {/* Background Slides */}
        {heroSlides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              idx === currentSlide
                ? "opacity-100 scale-100"
                : "opacity-0 scale-105"
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center brightness-110 contrast-110 saturate-125"
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white/10 via-transparent to-white/5 pointer-events-none" />
            <div className="absolute inset-0 bg-orange-600/35" />
            <div className="absolute inset-0 bg-orange-400/20" />
          </div>
        ))}

        {/* Floating Flowers - Hidden on mobile for performance */}
        <div className="absolute inset-0 pointer-events-none">
          <Flower delay={0} size={60} left={8} top={12} duration={8} />
          <Flower delay={1} size={50} left={88} top={15} duration={10} />
          <Flower delay={2} size={55} left={12} top={75} duration={9} />
          <Flower delay={2.5} size={48} left={70} top={82} duration={9} />
          <Flower delay={3} size={58} left={5} top={42} duration={12} />
          <Flower delay={1.8} size={48} left={92} top={48} duration={10} />
        </div>

        {/* Floating Particles - Fewer on mobile */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 bg-orange-200 rounded-full animate-float-particle ${
                i > 10 ? "hidden md:block" : ""
              }`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 5}s`,
                opacity: 0.3 + Math.random() * 0.4,
              }}
            />
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex items-center justify-center min-h-screen px-3 xs:px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-6xl mx-auto w-full">
            <div className="max-w-4xl mx-auto text-center">
              {/* Title */}
              <h1
                className={`
                  text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl 
                  font-bold text-white leading-tight 
                  mb-2 sm:mb-3 
                  px-2 sm:px-0
                  transform transition-all duration-1000 delay-300 
                  ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-10 opacity-0"
                  }
                `}
                style={{ textShadow: "0 4px 20px rgba(0,0,0,0.4)" }}
              >
                {heroSlides[currentSlide].title}
              </h1>

              {/* Subtitle */}
              <p
                className={`
                  text-lg xs:text-xl sm:text-2xl md:text-3xl 
                  font-semibold text-orange-100 
                  mb-3 sm:mb-4 
                  px-2 sm:px-0
                  transform transition-all duration-1000 delay-400 
                  ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-10 opacity-0"
                  }
                `}
              >
                {heroSlides[currentSlide].subtitle}
              </p>

              {/* Description */}
              <p
                className={`
                  text-sm xs:text-base sm:text-lg md:text-xl 
                  text-white leading-relaxed 
                  mb-6 sm:mb-8 
                  max-w-2xl mx-auto 
                  px-4 sm:px-6 md:px-0
                  transform transition-all duration-1000 delay-500 
                  ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-10 opacity-0"
                  }
                `}
              >
                {heroSlides[currentSlide].description}
              </p>

              {/* CTA Button */}
              <div
                className={`
                  flex justify-center 
                  mb-8 sm:mb-10 
                  px-4 sm:px-0
                  transform transition-all duration-1000 delay-700 
                  ${
                    isVisible
                      ? "translate-y-0 opacity-100"
                      : "translate-y-10 opacity-0"
                  }
                `}
              >
                <Link
                  to="/pandith-parichaya"
                  className="
                    group relative 
                    px-6 xs:px-8 sm:px-10 
                    py-3 xs:py-3.5 sm:py-4 
                    rounded-full
                    bg-gradient-to-r from-orange-500 to-orange-600
                    text-white font-bold 
                    text-base xs:text-lg sm:text-xl
                    shadow-[0_6px_20px_rgba(255,90,0,0.45)]
                    hover:shadow-[0_8px_28px_rgba(255,90,0,0.6)]
                    transition-all duration-300 
                    transform hover:-translate-y-1 hover:scale-105
                    active:scale-95
                    w-full xs:w-auto
                    max-w-sm xs:max-w-none
                  "
                >
                  <span className="relative z-10 flex items-center justify-center gap-2 xs:gap-3">
                    <span className="truncate">Connect Your Purohit Now</span>
                    <svg
                      className="w-5 h-5 xs:w-6 xs:h-6 flex-shrink-0 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>

                  <div
                    className="
                      absolute inset-0 rounded-full
                      bg-white/20 opacity-0
                      group-hover:opacity-20
                      transition-opacity duration-300
                    "
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-20 xs:bottom-24 left-1/2 transform -translate-x-1/2 flex gap-1.5 xs:gap-2 z-20">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`transition-all duration-300 ${
                idx === currentSlide
                  ? "w-8 xs:w-10 h-2.5 xs:h-3 bg-white rounded-full shadow-lg"
                  : "w-2.5 xs:w-3 h-2.5 xs:h-3 bg-white/60 rounded-full hover:bg-white/80 hover:scale-110 shadow-md"
              }`}
            />
          ))}
        </div>

        {/* Navigation Arrows - Hidden on very small screens */}
        <button
          onClick={() =>
            setCurrentSlide(
              (prev) => (prev - 1 + heroSlides.length) % heroSlides.length
            )
          }
          aria-label="Previous slide"
          className="
            absolute left-2 xs:left-4 top-1/2 -translate-y-1/2 z-20 
            w-10 h-10 xs:w-12 xs:h-12 
            bg-white/90 border-2 border-white rounded-full 
            flex items-center justify-center 
            text-orange-600 
            hover:bg-white 
            transition-all duration-300 
            hover:scale-110 
            active:scale-95
            shadow-xl
            hidden xs:flex
          "
        >
          <svg
            className="w-5 h-5 xs:w-6 xs:h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={() =>
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length)
          }
          aria-label="Next slide"
          className="
            absolute right-2 xs:right-4 top-1/2 -translate-y-1/2 z-20 
            w-10 h-10 xs:w-12 xs:h-12 
            bg-white/90 border-2 border-white rounded-full 
            flex items-center justify-center 
            text-orange-600 
            hover:bg-white 
            transition-all duration-300 
            hover:scale-110 
            active:scale-95
            shadow-xl
            hidden xs:flex
          "
        >
          <svg
            className="w-5 h-5 xs:w-6 xs:h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Scrolling Mantra Banner */}
        <div className="absolute bottom-0 left-0 right-0 bg-orange-700 py-2 xs:py-3 overflow-hidden border-t-2 border-orange-500 shadow-2xl">
          <div className="flex animate-scroll whitespace-nowrap">
            <div className="flex items-center">
              {/* Kannada */}
              <span className="inline-block px-4 xs:px-6 text-sm xs:text-base sm:text-lg text-white font-serif font-semibold">
                ಓಂ ಭೂರ್ಬುವಃ ಸ್ವಃ ತತ್ ಸವಿತುರ್ವರೇಣ್ಯಂ ಭರ್ಗೋ ದೇವಸ್ಯ ಧೀಮಹಿ ಧಿಯೋ ಯೋ
                ನಃ ಪ್ರಚೋ ದಯಾತ್
              </span>

              {/* Hindi */}
              <span className="inline-block px-4 xs:px-6 text-sm xs:text-base sm:text-lg text-white font-serif font-semibold">
                ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि धियो यो नः
                प्रचोदयात्
              </span>

              {/* English */}
              <span className="inline-block px-4 xs:px-6 text-sm xs:text-base sm:text-lg text-white font-serif font-semibold">
                Om Bhur Bhuvah Swaha • Tat Savitur Varenyam • Bhargo Devasya
                Dheemahi Dhiyo Yo Nah Prachodayat ॥
              </span>

              {/* Tamil */}
              <span className="inline-block px-4 xs:px-6 text-sm xs:text-base sm:text-lg text-white font-serif font-semibold">
                ஓம் பூர் புவஃ ஸ்வஃ தத் ஸவிதுர் வரேண்யம் பார்கோ தேவஸ்ய தீமஹி தியோ
                யோ நஃ ப்ரசோ தயாத்
              </span>

              {/* Telugu */}
              <span className="inline-block px-4 xs:px-6 text-sm xs:text-base sm:text-lg text-white font-serif font-semibold">
                ఓం భూర్భువః స్వః తత్ సవితుర్వరేణ్యం భర్గో దేవస్య ధీమహి ధియో యో
                నః ప్రచోదయాత్
              </span>

              {/* Repeat to make loop smooth */}
              <span className="inline-block px-4 xs:px-6 text-sm xs:text-base sm:text-lg text-white font-serif font-semibold">
                Om Bhur Bhuvah Swaha • Tat Savitur Varenyam • Bhargo Devasya
                Dheemahi
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PremiumHeroSection;
